import { useRef, useState } from 'react';
import { REGIONS } from '../game/content';
import type { OwnerProfile, RegionId } from '../game/types';
import { t } from '../i18n/en';

interface TitleScreenProps {
  canContinue: boolean;
  message: string | null;
  onContinue: () => void;
  onNewGame: (region: RegionId, owner: OwnerProfile) => void;
  onImport: (file: File) => void;
}

const SKIN_COLORS = ['#f1bd87', '#d99362', '#a96345', '#70402f', '#432a24'];
const HAIR_COLORS = ['#2c1a15', '#7b3f25', '#d8a052', '#5a4a42', '#d6d0c4'];

export function TitleScreen({ canContinue, message, onContinue, onNewGame, onImport }: TitleScreenProps) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [region, setRegion] = useState<RegionId>('sonoma');
  const [name, setName] = useState('Rowan');
  const [skin, setSkin] = useState(1);
  const [hair, setHair] = useState(0);
  const [hat, setHat] = useState(true);
  const [info, setInfo] = useState<'credits' | 'privacy' | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const avatarStyle = { '--skin': SKIN_COLORS[skin], '--hair': HAIR_COLORS[hair] } as React.CSSProperties;

  return (
    <main className="title-screen">
      <div className="title-vignette" />
      <section className="title-card" aria-labelledby="game-title">
        <div className="logo-bunch" aria-hidden="true"><i /><i /><i /><i /><i /><b /></div>
        <p className="eyebrow">A COZY WINERY TYCOON</p>
        <h1 id="game-title">{t('title')}</h1>
        <p className="title-subtitle">{t('subtitle')}</p>
        {!setupOpen ? (
          <div className="title-actions">
            <button className="button button-primary button-large" disabled={!canContinue} onClick={onContinue}>{t('continue')}</button>
            <button className="button button-large" onClick={() => setSetupOpen(true)}>{t('newGame')}</button>
            <button className="button button-quiet" onClick={() => fileRef.current?.click()}>{t('importSave')}</button>
            <input ref={fileRef} type="file" accept="application/json" hidden onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} />
          </div>
        ) : (
          <div className="new-game-setup">
            <div className="setup-heading">
              <button className="icon-button" aria-label="Back" onClick={() => setSetupOpen(false)}>←</button>
              <div><span className="eyebrow">NEW WINERY</span><h2>Choose your dirt</h2></div>
            </div>
            <div className="region-grid">
              {(Object.keys(REGIONS) as RegionId[]).map((id) => (
                <button key={id} className={`region-card ${region === id ? 'selected' : ''}`} aria-pressed={region === id} onClick={() => setRegion(id)}>
                  <span className="region-check">{region === id ? '◆' : '◇'}</span>
                  <strong>{REGIONS[id].name}</strong>
                  <small>{REGIONS[id].tagline}</small>
                  <p>{REGIONS[id].description}</p>
                </button>
              ))}
            </div>
            <div className="owner-builder">
              <div className={`pixel-avatar ${hat ? 'has-hat' : ''}`} style={avatarStyle} aria-label="Owner avatar preview"><span className="avatar-hat" /><span className="avatar-hair" /><span className="avatar-face" /><span className="avatar-body" /></div>
              <label>Owner name<input maxLength={18} value={name} onChange={(event) => setName(event.target.value)} /></label>
              <div className="swatch-control"><span>Skin</span>{SKIN_COLORS.map((color, index) => <button key={color} aria-label={`Skin tone ${index + 1}`} className={skin === index ? 'selected' : ''} style={{ background: color }} onClick={() => setSkin(index)} />)}</div>
              <div className="swatch-control"><span>Hair</span>{HAIR_COLORS.map((color, index) => <button key={color} aria-label={`Hair color ${index + 1}`} className={hair === index ? 'selected' : ''} style={{ background: color }} onClick={() => setHair(index)} />)}</div>
              <label className="check-label"><input type="checkbox" checked={hat} onChange={(event) => setHat(event.target.checked)} /> Essential harvest hat</label>
            </div>
            <button className="button button-primary button-large" disabled={!name.trim()} onClick={() => onNewGame(region, { name: name.trim(), skin, hair, hat })}>Lease the plot</button>
          </div>
        )}
        {message && <p className="title-message" role="status">{message}</p>}
        <footer className="title-footer">
          <button onClick={() => setInfo('credits')}>{t('credits')}</button><span>·</span><button onClick={() => setInfo('privacy')}>{t('privacy')}</button><span>·</span><span>v1.0</span>
        </footer>
      </section>
      {info && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setInfo(null)}>
          <section className="modal small-modal" role="dialog" aria-modal="true" aria-labelledby="info-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" aria-label="Close" onClick={() => setInfo(null)}>×</button>
            <h2 id="info-title">{info === 'credits' ? 'Credits' : 'Privacy'}</h2>
            {info === 'credits' ? <p>Design, code, procedural audio, and original pixel assets created for Crush Season. Estate artwork was generated for this project with OpenAI image generation and cleaned into the production composition.</p> : <p>Crush Season has no accounts, ads, tracking, or gameplay telemetry. Saves live in your browser’s IndexedDB until you export or clear them.</p>}
          </section>
        </div>
      )}
    </main>
  );
}
